<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue';

import { injectionKeys as litiumKeys } from '@motillo/drapejs-litium';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import DecorationCard from './DecorationCard.vue';
import DecorationTechniqueVariants from './DecorationTechniqueVariants.vue';

const { websiteText } = useWebsiteTexts();
const emit = defineEmits([
  'selected',
  'selectedVariant',
  'desiredWidthUpdated',
  'desiredHeightUpdated'
]);
const toLitiumMediaUrl = inject(litiumKeys.toLitiumMediaUrlKey, () => '');
const startAnimation = ref(false);

const props = defineProps({
  variants: {
    type: Array,
    required: true
  },
  techniques: {
    type: Array,
    required: true
  },
  selected: {
    type: String,
    default: ''
  },
  selectedVariant: {
    type: String,
    default: ''
  },
  selectedColors: {
    type: Object
  },
  selectedWidth: {
    type: Number,
    default: 0
  },
  selectedHeight: {
    type: Number,
    default: 0
  }
});

const mappedTechniques = computed(() => {
  return props.techniques.map((t) => {
    return {
      id: t.systemId,
      name: t.name || t.code,
      value: t.code,
      url: toLitiumMediaUrl(t.imageSystemId),
      techniqueTypeCode: t.techniqueTypeCode,
      landingPageLinkUrl: t.landingPageLinkUrl
    };
  });
});

const selectedTechnique = ref(props.selected);

const variants = computed(() => {
  return (
    props.techniques.find((t) => t.systemId === selectedTechnique.value)
      ?.variants || []
  );
});

const singleVariant = computed(() => {
  return variants.value.length === 1;
});

function hasCombinedVariants(technique) {
  return false && technique.techniqueTypeCode === 'PU LABEL';
}

function selectTechnique(id: string) {
  startAnimation.value = false;
  selectedTechnique.value = id;
  emit('selected', id);

  const selectedTechniqueObject = props.techniques.find(
    (t) => t.systemId == id
  );
  if (selectedTechniqueObject.techniqueTypeCode === 'PU LABEL') {
    for (var i = 0; i < variants.value.length; i++) {
      selectVariant(variants.value[i].systemId);
    }
  } else {
    selectVariant(variants.value[0].systemId);
  }

  setTimeout(() => {
    startAnimation.value = true;
  }, 0);
}

function selectVariant(id: string, colors = undefined) {
  const variant = variants.value.find((v) => v.systemId === id);
  const variantData = {
    brackets: variant.prices,
    id,
    addons: variant.addons
      .filter((a) => a.mandatory)
      .map((a) => {
        return {
          VariantSystemId: a.systemId,
          Quantity: 1,
          price: a.price,
          name: a.name,
          OnlyOnePerProductGroup: a.mandatory
        };
      }),
    supportedVariants: variant.supportedVariants
  };

  if (colors) {
    variantData.colors = colors;
  }

  emit('selectedVariant', variantData);
}

function desiredWidthUpdated(value: number) {
  emit('desiredWidthUpdated', value);
}

function desiredHeightUpdated(value: number) {
  emit('desiredHeightUpdated', value);
}

function techniqueOrder(idx) {
  const oneIndexed = idx + 1;
  return oneIndexed + Math.floor((oneIndexed - 1) / 2);
}

function variantOrder(idx) {
  const oneIndexed = idx + 1;
  return Math.ceil(oneIndexed / 2) * 3;
}

onMounted(() => {
  if (selectedTechnique.value && !singleVariant.value) {
    startAnimation.value = true;
  }
});

const notAvailableText = computed(() => {
  if (!singleVariant.value) {
    return '';
  }

  const selectedTechniqueVariant = variants.value[0];
  let notAvailableVariants = [];

  for (var i = 0; i < props.variants.length; i++) {
    const prodVariant = props.variants[i];
    if (
      selectedTechniqueVariant.supportedVariants.filter(
        (v) => v == prodVariant.systemId
      ).length == 0
    ) {
      notAvailableVariants.push(prodVariant.fields?.Color?.[0]?.name || '');
    }
  }

  if (notAvailableVariants.length == 0) {
    return '';
  }

  const notAvailableVariantsText = notAvailableVariants.join(', ');
  return websiteText('decoration__not_available', {
    decoration: selectedTechniqueVariant.name,
    variant: notAvailableVariantsText
  }).value;
});
</script>

<template>
  <div class="decoration-technique">
    <p class="decoration-technique__title">
      {{ websiteText('decoration__choose_technique').value }}
    </p>
    <div class="decoration-technique__container">
      <template
        :id="technique.id"
        v-for="(technique, idx) in mappedTechniques"
        :key="technique.id"
      >
        <DecorationCard
          :decoration="technique"
          :selected="technique.id === selectedTechnique"
          @selected="selectTechnique"
          :style="{ order: techniqueOrder(idx) }"
        >
        </DecorationCard>
        <div
          class="decoration-technique__decoration-variants-wrapper"
          :style="{ order: variantOrder(idx) }"
        >
          <DecorationTechniqueVariants
            :class="[
              'decoration-technique__decoration-variants',
              {
                'decoration-technique__decoration-variants--active':
                  startAnimation
              }
            ]"
            v-if="
              selectedTechnique === technique.id &&
              !singleVariant &&
              !hasCombinedVariants(technique)
            "
            :productVariants="props.variants"
            :variants="variants"
            :selected="selectedVariant"
            :colors="selectedColors"
            :desired-width="selectedWidth"
            :desired-height="selectedHeight"
            @selected="selectVariant"
            @desiredWidthUpdated="desiredWidthUpdated"
            @desiredHeightUpdated="desiredHeightUpdated"
          >
          </DecorationTechniqueVariants>
        </div>
      </template>
    </div>
    <div class="decoration-technique__warning" v-if="notAvailableText">
      <img
        class="decoration-technique__warning_icon"
        src="../../../static/icons/info.svg"
      />
      {{ notAvailableText }}
    </div>
  </div>
</template>

<style>
.decoration-technique {
  margin-bottom: 20px;
}

.decoration-technique__title {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-darker);
  user-select: none;
  margin-bottom: 5px;
}

.decoration-technique__container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
}

.decoration-technique__decoration-variants-wrapper {
  grid-column: span 2;
}

.decoration-technique__decoration-variants {
  opacity: 0;
  max-height: 0px;
  transition: opacity 0.4s ease-in, max-height 0.4s ease-in;
}

.decoration-technique__decoration-variants--active {
  opacity: 1;
  max-height: 1000px;
}

.decoration-technique__warning {
  display: flex;
  align-items: flex-start;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #5379ff;
}

.decoration-technique__warning_icon {
  width: 20px;
  height: 20px;
  padding-right: 4px;
  margin-top: 3px;
}
</style>
